// src/pages/platform-admin/SupervisorRanking.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    LinearProgress,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    TrendingUp,
    TrendingDown,
    TrendingFlat,
    CheckCircle,
    Warning,
    Info,
    Timeline,
    People,
    Article,
    Star,
    Psychology,
    EmojiEvents,
    BarChart,
    ShowChart,
    Assessment,
    School,
    Download,
    Schedule,
    Error as ErrorIcon,
    PlayArrow,
    Verified,
    Cancel,
    Pending,
    Whatshot,
    TextRotateUpSharp
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APISupervisorRankingList,
    APISupervisorRankingTopPerformers,
    APISupervisorRankingCalculate,
    APISupervisorRankingFinalize
} from '../../apiServices/APIs';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const SupervisorRanking = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rankings, setRankings] = useState([]);
    const [topPerformers, setTopPerformers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedPeriod, setSelectedPeriod] = useState('quarterly');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedCategory, setSelectedCategory] = useState('completion_rate');
    const [openCalculateDialog, setOpenCalculateDialog] = useState(false);
    const [calculateConfig, setCalculateConfig] = useState({ period: 'quarterly', year: new Date().getFullYear(), quarter: 1, month: 1 });
    const [calculating, setCalculating] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, [selectedPeriod, selectedYear, selectedCategory]);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await Promise.all([
                fetchRankings(),
                fetchTopPerformers()
            ]);
        } catch (err) {
            setError("Failed to load data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchRankings = async () => {
        const result = await apiService.list(APISupervisorRankingList, { 
            period: selectedPeriod, 
            year: selectedYear,
            category: selectedCategory 
        });
        if (result.success && result.data && isMounted.current) {
            setRankings(result.data.rankings || []);
            setSummary(result.data.summary);
        }
    };

    const fetchTopPerformers = async () => {
        const result = await apiService.list(APISupervisorRankingTopPerformers, { 
            period: selectedPeriod, 
            year: selectedYear 
        });
        if (result.success && result.data && isMounted.current) {
            setTopPerformers(result.data);
        }
    };

    const handleCalculateRankings = async () => {
        setCalculating(true);
        try {
            const formData = new FormData();
            formData.append('period', calculateConfig.period);
            formData.append('year', calculateConfig.year);
            if (calculateConfig.period === 'monthly') {
                formData.append('month', calculateConfig.month);
            } else if (calculateConfig.period === 'quarterly') {
                formData.append('quarter', calculateConfig.quarter);
            }
            
            const result = await apiService.update(APISupervisorRankingCalculate, formData);
            
            if (result && result.success) {
                showToast("Success", result.message, "success");
                fetchAllData();
                setOpenCalculateDialog(false);
            }
        } catch (error) {
            showToast("Error", "Failed to calculate rankings", "error");
        } finally {
            setCalculating(false);
        }
    };

    const handleFinalizeRankings = async () => {
        setFinalizing(true);
        try {
            const formData = new FormData();
            formData.append('period', selectedPeriod);
            formData.append('year', selectedYear);
            
            const result = await apiService.update(APISupervisorRankingFinalize, formData);
            
            if (result && result.success) {
                showToast("Success", result.message, "success");
                fetchAllData();
            }
        } catch (error) {
            showToast("Error", "Failed to finalize rankings", "error");
        } finally {
            setFinalizing(false);
        }
    };

    const getRankChangeIcon = (change) => {
        if (change > 0) return <TrendingUp sx={{ color: '#4caf50', fontSize: '1rem' }} />;
        if (change < 0) return <TrendingDown sx={{ color: '#f44336', fontSize: '1rem' }} />;
        return <TrendingFlat sx={{ color: '#ff9800', fontSize: '1rem' }} />;
    };

    const getCategoryIcon = (category) => {
        const icons = {
            'completion_rate': <CheckCircle />,
            'publication_support': <Article />,
            'student_satisfaction': <Star />,
            'research_impact': <TrendingUp />,
            'mentorship_quality': <Psychology />
        };
        return icons[category] || <Assessment />;
    };

    if (loading) {
        return <LoadingState message="Loading Supervisor Rankings..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Rankings" variant="research" fullPage retryText="Retry Loading" />;
    }

    const years = [2024, 2023, 2022, 2021];
    const categories = [
        { value: 'completion_rate', label: 'Completion Rate' },
        { value: 'publication_support', label: 'Publication Support' },
        { value: 'student_satisfaction', label: 'Student Satisfaction' },
        { value: 'research_impact', label: 'Research Impact' },
        { value: 'mentorship_quality', label: 'Mentorship Quality' }
    ];

    return (
        <>
            <FullWidthContainer >
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <PlatformAdminBar  notificationCount={19} />
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.primary.main, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <TextRotateUpSharp /> Supervisor Rankings
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                startIcon={<PlayArrow />} 
                                onClick={() => setOpenCalculateDialog(true)}
                                size={isMobile ? "small" : "medium"}
                            >
                                Calculate
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="success" 
                                startIcon={<Verified />} 
                                onClick={handleFinalizeRankings} 
                                disabled={finalizing}
                                size={isMobile ? "small" : "medium"}
                            >
                                Finalize
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={fetchAllData} 
                                sx={{ textTransform: 'none' }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Filters Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mt: 2, 
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'center', sm: 'flex-start' }
                    }}>
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                            <InputLabel>Period</InputLabel>
                            <Select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)} label="Period">
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="quarterly">Quarterly</MenuItem>
                                <MenuItem value="annual">Annual</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 100 } }}>
                            <InputLabel>Year</InputLabel>
                            <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} label="Year">
                                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                            <InputLabel>Category</InputLabel>
                            <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Category">
                                {categories.map(cat => <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </Paper>

                {/* Summary Cards */}
                {summary && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.info.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {summary.total_ranked}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Supervisors Ranked
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.success.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {summary.eligible_count}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Eligible
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.warning.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {summary.average_score}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Avg Score
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.secondary.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {summary.top_score}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Top Score
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Top Performers Section */}
                {topPerformers.length > 0 && (
                    <Paper sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: 3, 
                        borderRadius: 3, 
                        bgcolor: theme.palette.warning.light + '10',
                        backgroundColor: 'white'
                    }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            mb: 2, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <Whatshot color="error" /> Top Performers
                        </Typography>
                        <Grid container spacing={2}>
                            {topPerformers.map((performer, idx) => (
                                <Grid item xs={12} sm={6} md={3} key={idx}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: idx === 0 ? theme.palette.warning.light + '30' : 'transparent',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Chip 
                                                label={`#${performer.rank}`} 
                                                color="warning" 
                                                size="small" 
                                                sx={{ mb: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                            />
                                            <Avatar 
                                                src={performer.supervisor?.profile_image} 
                                                sx={{ width: { xs: 50, sm: 60 }, height: { xs: 50, sm: 60 }, mx: 'auto', mb: 1 }}
                                            >
                                                {performer.supervisor?.name?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="subtitle2" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}>
                                                {performer.supervisor?.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                {performer.supervisor?.university}
                                            </Typography>
                                            <Chip 
                                                label={`Score: ${performer.score}`} 
                                                size="small" 
                                                color="primary" 
                                                sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                )}
            </Box>
                {/* Rankings Table */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'auto',
                    backgroundColor: 'white',
                }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: { xs: 700, sm: 800, md: 900 } }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                    <TableCell><strong>Rank</strong></TableCell>
                                    <TableCell><strong>Supervisor</strong></TableCell>
                                    <TableCell><strong>University</strong></TableCell>
                                    <TableCell align="center"><strong>Score</strong></TableCell>
                                    <TableCell align="center"><strong>Change</strong></TableCell>
                                    <TableCell align="center"><strong>Status</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rankings.map((ranking) => (
                                    <TableRow key={ranking.id} hover>
                                        <TableCell>
                                            <Chip 
                                                label={`#${ranking.category_rank}`} 
                                                color={ranking.category_rank <= 3 ? 'success' : 'default'} 
                                                size="small"
                                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 2,
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                textAlign: { xs: 'center', sm: 'left' }
                                            }}>
                                                <Avatar 
                                                    src={ranking.supervisor?.profile_image}
                                                    sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}
                                                >
                                                    {ranking.supervisor?.name?.charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                    }}>
                                                        {ranking.supervisor?.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                        {ranking.supervisor?.academic_title}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            {ranking.supervisor?.university}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip 
                                                label={`${ranking.ranking_score}`} 
                                                size="small" 
                                                color={ranking.ranking_score >= 80 ? 'success' : ranking.ranking_score >= 60 ? 'warning' : 'info'}
                                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {ranking.rank_change && (
                                                <Chip 
                                                    icon={getRankChangeIcon(ranking.rank_change)}
                                                    label={`${Math.abs(ranking.rank_change)}`}
                                                    size="small"
                                                    color={ranking.rank_change_color}
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip 
                                                icon={ranking.eligibility_status === 'eligible' ? <Verified /> : ranking.eligibility_status === 'pending_verification' ? <Pending /> : <Cancel />}
                                                label={ranking.formatted_eligibility_status}
                                                size="small"
                                                color={ranking.eligibility_status_color}
                                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {rankings.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="text.secondary" sx={{ py: 4, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                                No ranking data available. Click "Calculate" to generate.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Calculate Rankings Dialog */}
                <Dialog 
                    open={openCalculateDialog} 
                    onClose={() => setOpenCalculateDialog(false)} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Calculate Supervisor Rankings
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Period</InputLabel>
                                <Select 
                                    value={calculateConfig.period} 
                                    onChange={(e) => setCalculateConfig({ ...calculateConfig, period: e.target.value })} 
                                    label="Period"
                                >
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                    <MenuItem value="annual">Annual</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Year</InputLabel>
                                <Select 
                                    value={calculateConfig.year} 
                                    onChange={(e) => setCalculateConfig({ ...calculateConfig, year: e.target.value })} 
                                    label="Year"
                                >
                                    {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                </Select>
                            </FormControl>
                            {calculateConfig.period === 'monthly' && (
                                <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                    <InputLabel>Month</InputLabel>
                                    <Select 
                                        value={calculateConfig.month} 
                                        onChange={(e) => setCalculateConfig({ ...calculateConfig, month: e.target.value })} 
                                        label="Month"
                                    >
                                        {[...Array(12)].map((_, i) => (
                                            <MenuItem key={i+1} value={i+1}>
                                                {new Date(2024, i, 1).toLocaleString('default', { month: 'long' })}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {calculateConfig.period === 'quarterly' && (
                                <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                    <InputLabel>Quarter</InputLabel>
                                    <Select 
                                        value={calculateConfig.quarter} 
                                        onChange={(e) => setCalculateConfig({ ...calculateConfig, quarter: e.target.value })} 
                                        label="Quarter"
                                    >
                                        <MenuItem value={1}>Q1 (Jan-Mar)</MenuItem>
                                        <MenuItem value={2}>Q2 (Apr-Jun)</MenuItem>
                                        <MenuItem value={3}>Q3 (Jul-Sep)</MenuItem>
                                        <MenuItem value={4}>Q4 (Oct-Dec)</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                            <Alert severity="info" sx={{ mt: 1 }}>
                                This will analyze all eligible supervisors and calculate rankings based on performance metrics.
                            </Alert>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={() => setOpenCalculateDialog(false)} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleCalculateRankings} 
                            variant="contained" 
                            disabled={calculating} 
                            startIcon={calculating ? <CircularProgress size={20} /> : <PlayArrow />}
                            size={isMobile ? "small" : "medium"}
                        >
                            {calculating ? 'Calculating...' : 'Calculate'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default SupervisorRanking;